import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';

type Severity = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  message: string;
  severity: Severity;
  key: number;
  open: boolean;
}

interface ToastContextType {
  showToast: (message: string, severity?: Severity) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

const MAX_VISIBLE = 3;
const AUTO_DISMISS_MS = 4000;

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const showToast = useCallback((message: string, severity: Severity = 'info') => {
    counterRef.current += 1;
    const newToast: Toast = { message, severity, key: counterRef.current, open: true };
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const showSuccess = useCallback((message: string) => showToast(message, 'success'), [showToast]);
  const showError   = useCallback((message: string) => showToast(message, 'error'),   [showToast]);

  const dismissToast = useCallback((key: number) => {
    setToasts((prev) => prev.map((t) => (t.key === key ? { ...t, open: false } : t)));
  }, []);

  const removeToast = useCallback((key: number) => {
    setToasts((prev) => prev.filter((t) => t.key !== key));
  }, []);

  const handleClose = useCallback((key: number) => (_: any, reason?: string) => {
    if (reason === 'clickaway') return;
    dismissToast(key);
  }, [dismissToast]);

  const visibleToasts = toasts.slice(-MAX_VISIBLE);

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError }}>
      {children}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: (theme) => theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pointerEvents: 'none',
        }}
      >
        {visibleToasts.map((toast) => (
          <Snackbar
            key={toast.key}
            open={toast.open}
            autoHideDuration={AUTO_DISMISS_MS}
            onClose={handleClose(toast.key)}
            TransitionComponent={Slide}
            TransitionProps={{
              onExited: () => removeToast(toast.key),
            }}
            sx={{
              position: 'static',
              transform: 'none',
              pointerEvents: 'auto',
            }}
          >
            <Alert
              onClose={() => dismissToast(toast.key)}
              severity={toast.severity}
              variant="filled"
              sx={{ width: '100%', fontWeight: 500, minWidth: 280 }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        ))}
      </Box>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
